"use client";

import { X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/scott/ui/elements/button/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/scott/ui/elements/dialog/dialog";
import { TextD2C } from "@/scott/ui/elements/text-d2c/text";
import { textD2CSizes } from "@/scott/ui/elements/text-d2c/text-d2c.constants";
import type { ProductTechnologiesProps } from "@/scott/ui/patterns/product-technologies/product-technologies";

type TechnologyItem = ProductTechnologiesProps["items"][number];

const TECHNOLOGY_PREVIEW_DESCRIPTION_MAX_LENGTH = 120;
const WORD_BOUNDARY_MIN_RATIO = 0.75;

export interface ProductTechnologySectionProps {
  technologies?: ProductTechnologiesProps;
  showMoreText?: string;
}

function getTechnologyPreviewDescription(description: string): string {
  if (description.length <= TECHNOLOGY_PREVIEW_DESCRIPTION_MAX_LENGTH) {
    return description;
  }

  const clipped = description
    .slice(0, TECHNOLOGY_PREVIEW_DESCRIPTION_MAX_LENGTH)
    .trimEnd();
  const lastWhitespaceIndex = clipped.lastIndexOf(" ");
  const minimumWordBoundaryIndex = Math.floor(
    TECHNOLOGY_PREVIEW_DESCRIPTION_MAX_LENGTH * WORD_BOUNDARY_MIN_RATIO
  );
  const preview =
    lastWhitespaceIndex > minimumWordBoundaryIndex
      ? clipped.slice(0, lastWhitespaceIndex).trimEnd()
      : clipped;

  return `${preview}...`;
}

export function ProductTechnologySection(
  props: Readonly<ProductTechnologySectionProps>
) {
  const [selectedTechnology, setSelectedTechnology] =
    useState<TechnologyItem | null>(null);
  const items = props.technologies?.items ?? [];

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
        {items.map((item) => {
          const showMoreText =
            item.microcopies.showMoreText ?? props.showMoreText;
          const previewDescription = getTechnologyPreviewDescription(
            item.microcopies.description
          );
          const canOpenModal = Boolean(
            item.showButton &&
              showMoreText &&
              item.microcopies.extendedDescription
          );

          return (
            <article
              className="flex h-full flex-col gap-3"
              key={item.microcopies.title}
            >
              <div className="flex-1">
                <TextD2C style="bold" variant="p-small">
                  {item.microcopies.title}
                </TextD2C>
                <TextD2C variant="p-small">{previewDescription}</TextD2C>
              </div>

              {canOpenModal ? (
                <Button
                  className={`h-auto self-start p-0 text-left font-brand font-normal underline hover:no-underline ${textD2CSizes["p-small"]}`}
                  onClick={() => setSelectedTechnology(item)}
                  type="button"
                  variant="link"
                >
                  {showMoreText}
                </Button>
              ) : null}
            </article>
          );
        })}
      </div>

      <Dialog
        onOpenChange={(open) => {
          if (!open) {
            setSelectedTechnology(null);
          }
        }}
        open={Boolean(selectedTechnology)}
      >
        <DialogContent
          aria-describedby={undefined}
          className="max-w-[930px] gap-0 overflow-hidden p-0"
          hideCloseButton
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogClose className="absolute top-4 right-4 z-10 bg-transparent opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>

          <div className="flex flex-col md:flex-row md:items-start">
            <div className="flex flex-shrink-0 justify-center px-6 pt-10 md:p-8">
              <img
                alt={selectedTechnology?.image.alt ?? ""}
                className="h-auto max-h-[265px] w-auto max-w-[265px] object-contain md:max-h-none md:w-[160px] md:max-w-none"
                src={selectedTechnology?.image.src ?? ""}
              />
            </div>

            <div className="flex flex-col gap-3 px-6 pt-8 pb-8 md:gap-5 md:pt-8 md:pr-8 md:pb-8 md:pl-0">
              <DialogTitle
                className={`font-bold font-brand text-black ${textD2CSizes.p}`}
              >
                {selectedTechnology?.microcopies.title}
              </DialogTitle>
              <TextD2C style="no-mb" variant="p">
                {selectedTechnology?.microcopies.extendedDescription}
              </TextD2C>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
